/* Author-facing bookshelf chip for OHAS status.
 * Renders "OHAS D3" pill on certified books; on uncertified books it
 * paints a "Certify" button that runs the scorer server-side.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, ShieldCheck, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const LEVEL_TONES = {
  D0: 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40',
  D1: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/40',
  D2: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  D3: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/40',
  D4: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40',
};

export default function BookshelfOhasChip({ bookId, level, onCertified }) {
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(level || null);

  const certify = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/authors/me/ohas/${bookId}/certify`);
      const lvl = r.data?.manifest?.level;
      setCurrent(lvl);
      toast.success(`Certified at ${lvl}.`);
      onCertified?.(lvl);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Certify failed.');
    } finally { setBusy(false); }
  };

  if (current) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${LEVEL_TONES[current] || LEVEL_TONES.D0}`}
        title={`OHAS ${current} — human authorship verified`}
        data-testid={`bookshelf-ohas-chip-${bookId}`}
      >
        <Award className="h-2.5 w-2.5" />
        OHAS {current}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={certify}
      disabled={busy}
      data-testid={`bookshelf-ohas-certify-${bookId}`}
      className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary hover:bg-primary/20 disabled:opacity-60"
      title="Run OHAS scorer against your manuscript evidence"
    >
      {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <ShieldCheck className="h-2.5 w-2.5" />}
      {busy ? 'Certifying' : 'Certify OHAS'}
    </button>
  );
}
